// Static configuration for the property-listing-request wizard: ownership-role
// options (values mirror the seller-service PropertyOwnershipRole enum), the
// ordered step list driving the progress rail, and title-deed upload limits
// (mirrors the seller-service title-deed validation).

import type { InjectionKey, Ref } from 'vue'
import type { PiIconName } from '@uiKit/elements/PiIcon.vue'
import type { SelectOption } from '@shared/types/form'

export const OWNERSHIP_ROLE = {
  SOLE_OWNER: 'sole_owner',
  CO_OWNER: 'co_owner',
  REAL_ESTATE_AGENT: 'real_estate_agent',
  AUTHORISED_REPRESENTATIVE: 'authorised_representative',
} as const

export type OwnershipRole = (typeof OWNERSHIP_ROLE)[keyof typeof OWNERSHIP_ROLE]

// Values mirror the shared `property_type` PG enum (seller-service PropertyType).
// Required at submit: `properties.property_type` is NOT NULL, so a request without
// one can never be promoted into a property — and once a request reaches owner
// onboarding there is no revision path left to add it.
//
// The full enum lives here because read-only surfaces still have to label requests
// submitted under any of these values; what a seller may *pick* today is the
// narrower `SELECTABLE_PROPERTY_TYPES` below.
export const PROPERTY_TYPE = {
  RESIDENTIAL: 'residential',
  COMMERCIAL: 'commercial',
  APARTMENT: 'apartment',
  VILLA: 'villa',
  BUILDING: 'building',
  STORE: 'store',
  WORKSHOP: 'workshop',
  LAND: 'land',
  UNIT: 'unit',
} as const

export type PropertyType = (typeof PROPERTY_TYPE)[keyof typeof PROPERTY_TYPE]

const PROPERTY_TYPE_LABELS: Record<PropertyType, string> = {
  [PROPERTY_TYPE.RESIDENTIAL]: 'Residential',
  [PROPERTY_TYPE.COMMERCIAL]: 'Commercial',
  [PROPERTY_TYPE.APARTMENT]: 'Apartment',
  [PROPERTY_TYPE.VILLA]: 'Villa',
  [PROPERTY_TYPE.BUILDING]: 'Building',
  [PROPERTY_TYPE.STORE]: 'Store',
  [PROPERTY_TYPE.WORKSHOP]: 'Workshop',
  [PROPERTY_TYPE.LAND]: 'Land',
  [PROPERTY_TYPE.UNIT]: 'Unit',
}

// Only these two are offered on the create / revise request. The platform lists
// individual units and villas today; the remaining enum values exist for older
// requests and for admin-created properties, so they stay labelled above but are
// not selectable here. A request being revised whose stored type is outside this
// set seeds empty and the seller re-picks (see the store's
// `toSelectablePropertyType`).
export const SELECTABLE_PROPERTY_TYPES = [PROPERTY_TYPE.UNIT, PROPERTY_TYPE.VILLA] as const

export type SelectablePropertyType = (typeof SELECTABLE_PROPERTY_TYPES)[number]

const SELECTABLE_PROPERTY_TYPE_VALUES = new Set<string>(SELECTABLE_PROPERTY_TYPES)

export function isSelectablePropertyType(value: string | null): value is SelectablePropertyType {
  return value !== null && SELECTABLE_PROPERTY_TYPE_VALUES.has(value)
}

export const PROPERTY_TYPE_OPTIONS: { label: string; value: SelectablePropertyType }[] =
  SELECTABLE_PROPERTY_TYPES.map((value) => ({ label: PROPERTY_TYPE_LABELS[value], value }))

function isPropertyType(value: string): value is PropertyType {
  return value in PROPERTY_TYPE_LABELS
}

// Read-only surfaces (the request detail screen) render the stored enum value,
// which may be a type the wizard no longer offers — hence the full label map
// above rather than one derived from the selectable options. Unknown values fall
// back to a prettified form of the raw value rather than disappearing.
export function propertyTypeLabelOf(value: string | null): string | null {
  if (!value) return null
  return isPropertyType(value) ? PROPERTY_TYPE_LABELS[value] : prettifyEnumValue(value)
}

// What the title deed itself prints — DLD's sub-type under the main type above (a
// Unit is a Flat, a Villa is a Townhouse). Values mirror seller-service
// `PropertyTypeOnDeed`. Required at submit, and it must sit under the picked type:
// seller-service refuses a crossed pair with `PROPERTY_TYPE_ON_DEED_MISMATCH`.
export const PROPERTY_TYPE_ON_DEED = {
  FLAT: 'flat',
  APARTMENT: 'apartment',
  UNIT: 'unit',
  HOTEL_APARTMENT: 'hotel_apartment',
  VILLA: 'villa',
  TOWNHOUSE: 'townhouse',
} as const

export type PropertyTypeOnDeed = (typeof PROPERTY_TYPE_ON_DEED)[keyof typeof PROPERTY_TYPE_ON_DEED]

const PROPERTY_TYPE_ON_DEED_LABELS: Record<PropertyTypeOnDeed, string> = {
  [PROPERTY_TYPE_ON_DEED.FLAT]: 'Flat',
  [PROPERTY_TYPE_ON_DEED.APARTMENT]: 'Apartment',
  [PROPERTY_TYPE_ON_DEED.UNIT]: 'Unit',
  [PROPERTY_TYPE_ON_DEED.HOTEL_APARTMENT]: 'Hotel Apartment',
  [PROPERTY_TYPE_ON_DEED.VILLA]: 'Villa',
  [PROPERTY_TYPE_ON_DEED.TOWNHOUSE]: 'Townhouse',
}

// Which deed types sit under each selectable type — the second dropdown's options
// follow the first, so the seller can never pick a crossed pair.
const DEED_TYPES_BY_PROPERTY_TYPE: Record<SelectablePropertyType, readonly PropertyTypeOnDeed[]> = {
  [PROPERTY_TYPE.UNIT]: [
    PROPERTY_TYPE_ON_DEED.FLAT,
    PROPERTY_TYPE_ON_DEED.APARTMENT,
    PROPERTY_TYPE_ON_DEED.UNIT,
    PROPERTY_TYPE_ON_DEED.HOTEL_APARTMENT,
  ],
  [PROPERTY_TYPE.VILLA]: [PROPERTY_TYPE_ON_DEED.VILLA, PROPERTY_TYPE_ON_DEED.TOWNHOUSE],
}

export function propertyTypeOnDeedOptionsFor(
  propertyType: SelectablePropertyType | null,
): { label: string; value: PropertyTypeOnDeed }[] {
  if (!propertyType) return []
  return DEED_TYPES_BY_PROPERTY_TYPE[propertyType].map((value) => ({
    label: PROPERTY_TYPE_ON_DEED_LABELS[value],
    value,
  }))
}

export function isPropertyTypeOnDeed(value: string | null): value is PropertyTypeOnDeed {
  return value !== null && value in PROPERTY_TYPE_ON_DEED_LABELS
}

export function isDeedTypeUnder(
  propertyType: SelectablePropertyType | null,
  propertyTypeOnDeed: PropertyTypeOnDeed | null,
): boolean {
  if (!propertyType || !propertyTypeOnDeed) return false
  return DEED_TYPES_BY_PROPERTY_TYPE[propertyType].includes(propertyTypeOnDeed)
}

export function propertyTypeOnDeedLabelOf(value: string | null): string | null {
  if (!value) return null
  return isPropertyTypeOnDeed(value)
    ? PROPERTY_TYPE_ON_DEED_LABELS[value]
    : prettifyEnumValue(value)
}

// "Unit - Flat": our type first, then what the deed prints. A request submitted
// before the deed type was asked for has none, and reads as the bare type.
export function propertyTypeDisplayOf(
  propertyType: string | null,
  propertyTypeOnDeed: string | null,
): string | null {
  const typeLabel = propertyTypeLabelOf(propertyType)
  const deedLabel = propertyTypeOnDeedLabelOf(propertyTypeOnDeed)
  if (!typeLabel || !deedLabel) return typeLabel
  return `${typeLabel} - ${deedLabel}`
}

// The role cards carry Material / kit glyphs from the design, not PrimeIcons —
// see `OwnershipRoleCards.vue` for the key → SFC map. A key rather than the SFC
// itself keeps this constants module free of Vue component imports.
export type OwnershipRoleIcon = 'people' | 'person' | 'shieldPerson' | 'building'

export interface OwnershipRoleOption {
  value: OwnershipRole
  title: string
  description: string
  icon: OwnershipRoleIcon
  // Shown but not selectable while the platform onboards owners only — agent and
  // representative listings need a POA / broker-licence check that isn't built yet.
  isAvailable: boolean
}

// Array order is the on-screen card order (Figma 2×2: sole, co-owner, authorised
// representative, agent). Agent and representative stay listed but unselectable
// until those listing paths exist.
export const OWNERSHIP_ROLE_OPTIONS: OwnershipRoleOption[] = [
  {
    value: OWNERSHIP_ROLE.SOLE_OWNER,
    title: 'Sole owner',
    description: 'You hold 100% on the title deed.',
    icon: 'person',
    isAvailable: true,
  },
  {
    value: OWNERSHIP_ROLE.CO_OWNER,
    title: 'Co-owner',
    description: 'You share ownership. Every owner signs.',
    icon: 'people',
    isAvailable: true,
  },
  {
    value: OWNERSHIP_ROLE.AUTHORISED_REPRESENTATIVE,
    title: 'Authorised representative',
    description: 'You act for the owner(s) under a Power of Attorney.',
    icon: 'shieldPerson',
    isAvailable: false,
  },
  {
    value: OWNERSHIP_ROLE.REAL_ESTATE_AGENT,
    title: 'Real estate agent',
    description: "You're a RERA-licensed broker acting for the owner.",
    icon: 'building',
    isAvailable: false,
  },
]

const OWNERSHIP_ROLE_LABELS: Record<string, string> = Object.fromEntries(
  OWNERSHIP_ROLE_OPTIONS.map((option) => [option.value, option.title]),
)

export function ownershipRoleLabelOf(value: string | null): string | null {
  if (!value) return null
  return OWNERSHIP_ROLE_LABELS[value] ?? prettifyEnumValue(value)
}

// snake_case enum value → "Title Case" words. Shared by both label lookups above
// as the unknown-value fallback so a backend enum addition still reads sensibly.
function prettifyEnumValue(value: string): string {
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// DOM id of the wizard shell's footer strip. Shared so the shell that renders the
// strip and the footer that teleports into it can never drift apart.
export const WIZARD_ACTION_BAR_ID = 'listing-wizard-actions'

/**
 * "Save as draft", provided by the wizard shell to the footer every step renders.
 * Injected rather than passed as props because the footer sits under four
 * different step forms that have no stake in it — threading it through each would
 * be four forms and four containers carrying a prop none of them reads.
 */
export interface WizardDraftSave {
  /** The flow can be saved as a draft at all (create, not revise). */
  isAvailable: Readonly<Ref<boolean>>
  /** Something has changed since the last save — otherwise there is nothing to save. */
  hasChanges: Readonly<Ref<boolean>>
  /** A draft exists and the form matches it — the work is safe as it stands. */
  isSaved: Readonly<Ref<boolean>>
  isSaving: Readonly<Ref<boolean>>
  save: () => void
}

export const WIZARD_DRAFT_SAVE_KEY: InjectionKey<WizardDraftSave> = Symbol('wizardDraftSave')

/**
 * Whether the wizard flow is finished, provided by the shell to every step.
 * On the create flow the last step stays mounted behind the submitted modal, so
 * its footer would otherwise teleport into the action bar on top of the shell's
 * own success actions and overlap them. A step footer reads this and renders
 * nothing once the request is in, leaving the bar to the shell's exits alone.
 */
export const WIZARD_COMPLETED_KEY: InjectionKey<Readonly<Ref<boolean>>> = Symbol('wizardCompleted')

export type ListingStepKey = 'ownership' | 'titleDeed' | 'verify' | 'pricing'

export const STEP_KEYS: ListingStepKey[] = ['ownership', 'titleDeed', 'verify', 'pricing']

export interface WizardStepMeta {
  key: ListingStepKey
  title: string
  subtitle: string
  icon: PiIconName
}

// Horizontal stepper labels name the stage, not the action — the step screen
// carries the instruction. Subtitle/icon stay for surfaces that still want them.
export const LISTING_STEPS: WizardStepMeta[] = [
  {
    key: 'ownership',
    title: 'Ownership & basics',
    subtitle: 'Your role on the property',
    icon: 'verified',
  },
  {
    key: 'titleDeed',
    title: 'Documents',
    subtitle: 'Title deed and supporting files',
    icon: 'file',
  },
  {
    key: 'verify',
    title: 'Verify property',
    subtitle: 'Confirm extracted details',
    icon: 'chart-bar',
  },
  {
    key: 'pricing',
    title: 'Listing & pricing',
    subtitle: 'Asking price and settlement',
    icon: 'check-square',
  },
]

// The pricing step's last CTA reads as a review gate, not a submit: pressing it
// takes the seller to the final check screen where they confirm and send.
export const PRICING_CTA_LABEL = 'Final check'

// Icon keys, not SFCs — the pricing card maps these to PrimeIcons glyphs, the
// same way the ownership role cards carry kit glyphs by key.
export type PricingInsightIcon = 'chart' | 'exchange' | 'clock'

// The "How your price shapes the sale" card. Static explainer copy: the seller
// is setting a number they may never revise, so the three things that change
// because of it are stated next to the field rather than left to support docs.
export const PRICING_SALE_INSIGHTS: {
  icon: PricingInsightIcon
  title: string
  body: string
}[] = [
  {
    icon: 'chart',
    title: 'Priced at or below market? It funds faster.',
    body: 'Investors compare price per sq m and rental yield before they invest.',
  },
  {
    icon: 'exchange',
    title: 'We may propose a different price',
    body: 'If so, you accept or decline it from your sell request. Declining withdraws the request.',
  },
  {
    icon: 'clock',
    title: 'A 90-day funding window',
    body: 'The clock starts once investors can invest in your property.',
  },
]

// Step 3 is a mini-wizard of its own: the design splits the extracted details
// into six sections, each with its own card, tracker row and "Save & continue"
// CTA, so the seller folds one section away before the next appears. The order
// here drives the tracker rail, the numbered badges and the CTA sequence; the
// final section's CTA submits the step (its label matches the pre-revamp form's,
// because ticking the declarations is the whole point of landing there).
export type VerifySectionKey =
  | 'owners'
  | 'property'
  | 'deed'
  | 'location'
  | 'tenancy'
  | 'eligibility'

export interface VerifySectionMeta {
  key: VerifySectionKey
  // Tracker-rail label and section-card heading — same word in the design.
  title: string
  // One line under the heading, saying what the section asks for.
  description: string
  // CTA shown while this section is active.
  ctaLabel: string
}

export const VERIFY_SECTIONS: VerifySectionMeta[] = [
  {
    key: 'owners',
    title: 'Owners',
    description:
      "Everyone on the deed signs with UAE PASS, so we need each owner's contact details. Names and shares come from the deed.",
    ctaLabel: 'Save & continue to Property',
  },
  {
    key: 'property',
    title: 'Property',
    description:
      "What the property is. Edit anything we misread — we'll show our team what changed.",
    ctaLabel: 'Save & continue to Deed & registration',
  },
  {
    key: 'deed',
    title: 'Deed & registration',
    description:
      'The registry entry and mortgage details printed on your deed. Correct anything we misread.',
    ctaLabel: 'Save & continue to Location & registration',
  },
  {
    key: 'location',
    title: 'Location & registration',
    description: 'Where the property is, and how the municipality records it.',
    ctaLabel: 'Save & continue to Tenancy',
  },
  {
    key: 'tenancy',
    title: 'Tenancy',
    description: 'The terms we read from your Ejari certificate.',
    ctaLabel: 'Save & continue to Eligibility',
  },
  {
    key: 'eligibility',
    title: 'Eligibility',
    description: 'Five declarations that decide whether we can list the property.',
    ctaLabel: 'Looks Good',
  },
]

export interface OwnershipPrepareItem {
  title: string
  description: string
  icon: PiIconName
}

// Step-1 "Before you start" checklist — what to have ready, not what to upload yet.
export const OWNERSHIP_PREPARE_ITEMS: OwnershipPrepareItem[] = [
  { title: 'Title deed', description: 'PDF or a clear photo', icon: 'file' },
  { title: 'Ejari contract', description: 'Only if the property is rented', icon: 'file' },
  { title: 'Emirates ID', description: 'For each owner', icon: 'id-card' },
  { title: 'UAE PASS', description: 'Every owner e-signs with it', icon: 'shield' },
]

// Step-2 "For a clean read" rail — what makes an upload readable by the document
// reader first time, so the seller isn't asked to re-shoot a blurry deed.
// `sparkle` is the kit's single four-point SparkleIcon (PrimeIcons only has the
// three-star cluster); every other key is a PrimeIcons name.
export interface DocumentReadTip {
  title: string
  description: string
  icon: PiIconName | 'sparkle'
}

export const DOCUMENT_READ_TIPS: DocumentReadTip[] = [
  {
    title: 'Upload every page',
    description: 'Include the QR code and all owner names.',
    icon: 'qrcode',
  },
  {
    title: "Make sure it's readable",
    description: 'No cropping, glare or blur on photos.',
    icon: 'eye',
  },
  { title: 'Use the latest deed', description: 'It must match the current owners.', icon: 'file' },
]

// The same rail while the deed is being read — the tips are moot once the file
// is in, so it says what the read is buying the seller instead.
export const WHILE_WE_READ_TIPS: DocumentReadTip[] = [
  {
    title: 'Less typing, fewer errors',
    description: 'Details come straight from the DLD record on your deed.',
    icon: 'sparkle',
  },
  {
    title: 'Faster review',
    description: 'Our team checks the same source, so approvals move quicker.',
    icon: 'shield',
  },
]

// The review state's second rail card — what happens on the next step, so the
// seller understands the summary is a read-out, not yet another form to fill.
export interface DocumentsNextStepCard {
  title: string
  itemTitle: string
  itemDescription: string
}

export const DOCUMENTS_NEXT_STEP_CARD: DocumentsNextStepCard = {
  title: 'Next: check the details',
  itemTitle: 'One place to check',
  itemDescription:
    'Verify property shows everything we read, next to your deed. Edit anything there.',
}

// The step-2 rented question. The answer is required (it gates the Ejari upload
// and, server-side, the rent terms), so the form starts unanswered rather than
// defaulting to "not rented".
export interface RentedStatusOption {
  value: boolean
  title: string
  description: string
}

export const RENTED_STATUS_OPTIONS: RentedStatusOption[] = [
  {
    value: true,
    title: "Yes, there's a tenant",
    description: 'Upload the Ejari below.',
  },
  {
    value: false,
    title: "No, it's not rented",
    description: 'Vacant or owner-occupied.',
  },
]

// Exported so the file-size formatter in utils/fileDisplayUtils.ts divides by the
// same unit the ceilings below are built from — otherwise a file could display
// as "10.0 MB" while failing a "Maximum 10 MB" check.
export const BYTES_PER_KB = 1024
export const BYTES_PER_MB = BYTES_PER_KB * BYTES_PER_KB

export const TITLE_DEED_MAX_FILE_SIZE_MB = 10
export const TITLE_DEED_MAX_FILE_SIZE_BYTES = TITLE_DEED_MAX_FILE_SIZE_MB * BYTES_PER_MB
export const TITLE_DEED_ACCEPT =
  '.png,.jpg,.jpeg,.heif,.heic,.pdf,image/png,image/jpeg,image/heif,image/heic,application/pdf'

// Mirrors the seller-service rental-agreement limits. Kept as its own set of
// constants rather than aliases of the deed's so tightening one never silently
// tightens the other.
export const RENTAL_AGREEMENT_MAX_FILE_SIZE_MB = 10
export const RENTAL_AGREEMENT_MAX_FILE_SIZE_BYTES = RENTAL_AGREEMENT_MAX_FILE_SIZE_MB * BYTES_PER_MB
export const RENTAL_AGREEMENT_ACCEPT =
  '.png,.jpg,.jpeg,.heif,.heic,.pdf,image/png,image/jpeg,image/heif,image/heic,application/pdf'

// What the title-deed reading card's checklist walks through while the read is
// in flight, in the order the extraction conceptually works. The read is one
// request with no per-stage events, so these pace as an illusion of progress
// (see DocumentReadingCard) — they name what the seller's deed is being used
// for, which is what the Verify step will actually show them.
export const TITLE_DEED_READING_STAGES = [
  'Recognised as a DLD title deed',
  'Reading owners and ownership shares',
  'Property type, area and unit number',
  'Plot, municipality and title deed numbers',
]

export const DEFAULT_LISTING_CURRENCY = 'AED'

// Every listing is UAE property today, so the country selector preselects this
// ISO-2 code from the countries lookup instead of making the seller find it.
export const DEFAULT_LISTING_COUNTRY_ISO2 = 'AE'

// Field length ceilings — mirror the seller-service CreateListingRequestRequestDto
// column limits so the client rejects over-length input before the round trip.
export const LISTING_FIELD_MAX_LENGTH = {
  certificateNumber: 100,
  certificateYear: 4,
  propertyName: 255,
  propertyNumber: 50,
  buildingName: 255,
  buildingNumber: 50,
  locationArea: 255,
  plotNumber: 50,
  municipalityNumber: 100,
  addressLine1: 255,
  city: 100,
  ownerFullName: 255,
  ownerEmail: 255,
  ownerMobileNumber: 20,
  ownerEmiratesId: 15,
  tenantName: 255,
  registrationNumber: 50,
  mortgageeName: 255,
} as const

// The monthly rent is shown, never entered: it is the annual figure spread evenly
// over the calendar year. The client computes it for display only — seller-service
// derives the stored value from `annualRentAmount` and does not accept a monthly
// figure at all, so the two can never disagree.
export const MONTHS_PER_YEAR = 12

// Owner mobile numbers are entered through the tel input in international mode
// and submitted as E.164 — mirrors the seller-service OwnerInputDto regex.
export const OWNER_MOBILE_DEFAULT_COUNTRY = 'AE'
export const OWNER_MOBILE_E164_REGEX = /^\+[1-9]\d{7,14}$/

// Emirates IDs are entered with or without the card's display dashes and submitted
// digits-only — mirrors the seller-service OwnerInputDto regex, which in turn
// mirrors the emirates_id CHECK constraint.
export const OWNER_EMIRATES_ID_REGEX = /^\d{15}$/
export const OWNER_EMIRATES_ID_PLACEHOLDER = '784-1990-1234567-1'

// Whether a declared owner is a person or a company. Mirrors
// `property_owners.property_owner_type` and seller-service's `PropertyOwnerType`.
// The value decides which onboarding flow and identity-verification level the
// owner's invite puts them on, so it has to be right before the link goes out.
export const OWNER_TYPE = {
  INDIVIDUAL: 'individual',
  COMPANY: 'company',
} as const

export type OwnerType = (typeof OWNER_TYPE)[keyof typeof OWNER_TYPE]

// Company is listed but not selectable: company-owned listings need a corporate
// onboarding + identity-verification path the platform doesn't run yet, so a
// company owner declared here would stall at the invite. Kept visible rather than
// removed so the seller can see it's coming, and so the enum stays legible next to
// the OCR path, which can still read `company` off a title deed.
export const OWNER_TYPE_OPTIONS: SelectOption[] = [
  { label: 'Individual', value: OWNER_TYPE.INDIVIDUAL },
  { label: 'Company (coming soon)', value: OWNER_TYPE.COMPANY, disabled: true },
]

// The mortgage / encumbrance status a title deed carries. Mirrors
// `property_listing_requests.mortgage_status` and seller-service's
// `PropertyMortgageStatus`.
//
// Only `mortgaged` and `clear` exist: the reader is instructed to answer
// `mortgaged` when the deed names a lender holding a charge, `clear` only when
// the deed says so in as many words, and to omit the field otherwise. So an
// unread status is empty, never a third value — a deed that is silent on
// encumbrances is not evidence of a clear title.
export const MORTGAGE_STATUS = {
  MORTGAGED: 'mortgaged',
  CLEAR: 'clear',
} as const

export type MortgageStatus = (typeof MORTGAGE_STATUS)[keyof typeof MORTGAGE_STATUS]

export const MORTGAGE_STATUS_OPTIONS: SelectOption[] = [
  { label: 'No mortgage — clear title', value: MORTGAGE_STATUS.CLEAR },
  { label: 'Mortgaged', value: MORTGAGE_STATUS.MORTGAGED },
]

// Read-only surfaces (and the folded section summary) render the stored value;
// an unset status reads as blank so the caller can omit the row entirely.
export function mortgageStatusLabelOf(value: string | null | undefined): string {
  return MORTGAGE_STATUS_OPTIONS.find((option) => option.value === value)?.label ?? ''
}

const MORTGAGE_STATUS_VALUES = new Set<string>(Object.values(MORTGAGE_STATUS))

// A read-only narrowing for the extraction parser: the reader is free to answer
// anything, and only the two known values may reach a form select.
export function isMortgageStatus(value: string | null): value is MortgageStatus {
  return value !== null && MORTGAGE_STATUS_VALUES.has(value)
}

// Positive money amount, up to 2 decimals, rejects zero — mirrors the BE
// POSITIVE_MONEY_REGEX. Area is non-negative with up to 2 decimals (AREA_REGEX).
export const LISTING_PRICE_REGEX = /^(?!0+(\.0+)?$)\d{1,16}(\.\d{1,2})?$/
// What the price field may hold mid-edit: digits, the grouping commas it puts
// back on blur, and one decimal point with up to 2 places. Anything else is
// refused at the keystroke rather than reported after the fact.
export const LISTING_PRICE_INPUT_REGEX = /^[\d,]*(\.\d{0,2})?$/
// Smallest asking price the platform takes on, in AED (the only listing currency).
// Client-side only for now — seller-service accepts any positive amount.
export const MIN_ASKING_PRICE_AED = 100_000
export const LISTING_AREA_REGEX = /^\d{1,8}(\.\d{1,2})?$/
export const OWNERSHIP_PERCENTAGE_REGEX = /^(100(\.0{1,2})?|\d{1,2}(\.\d{1,2})?)$/

// DLD identifiers (title deed, property, plot and municipality numbers): digits,
// optionally split by "-" or "/" as the documents print them (e.g. 868-74884).
// Must start and end with a digit, so "-" or "12-" alone is refused.
export const LISTING_IDENTIFIER_REGEX = /^\d+(?:[-/]\d+)*$/
// The year part of a title deed certificate number ("243738/2025" → 2025). Four
// digits and nothing else: the server's `@Matches(/^\d{4}$/)` is the same rule,
// and it derives the same year from the certificate number when extracting.
export const DEED_YEAR_REGEX = /^\d{4}$/
// Property and municipality numbers may also carry Latin letters (e.g. "A-101",
// "MUN-2023/45"). Same separator rules: no leading, trailing or doubled "-" / "/".
export const LISTING_ALPHANUMERIC_IDENTIFIER_REGEX = /^[A-Za-z\d]+(?:[-/][A-Za-z\d]+)*$/
// A person's name or a place name: letters (any script, so Arabic names pass),
// with the spaces, dots, apostrophes and hyphens real names carry.
export const LISTING_NAME_REGEX = /^\p{L}[\p{L}\s.'-]*$/u
// A tenant may be a company ("Acme FZ-LLC", "Smith & Sons"), so "&", "," and
// brackets are allowed on top of the name characters. Still no digits.
export const LISTING_TENANT_NAME_REGEX = /^\p{L}[\p{L}\s.'&,()-]*$/u
// Free-text fields (property, building, area, address) may hold digits —
// "Tower 1", "Al Barsha 1" — but must say something in words.
export const CONTAINS_LETTER_REGEX = /\p{L}/u

// What each field may hold mid-edit. Checked against the value a keystroke or
// paste would produce, so a refused character never reaches the screen; the
// full-format regexes above still run on blur and submit.
export const INPUT_CHARSET = {
  identifier: /^[\d/-]*$/,
  alphanumericIdentifier: /^[A-Za-z\d/-]*$/,
  emiratesId: /^[\d-]*$/,
  // The year of a title deed certificate number: four digits, no separators.
  year: /^\d{0,4}$/,
  area: /^\d{0,8}(\.\d{0,2})?$/,
  percentage: /^\d{0,3}(\.\d{0,2})?$/,
  money: /^\d*(\.\d{0,2})?$/,
  name: /^[\p{L}\s.'-]*$/u,
  tenantName: /^[\p{L}\s.'&,()-]*$/u,
} as const

export const FULL_OWNERSHIP_PERCENTAGE = 100
